// ============================================================
// WebRTC STUN / TURN configuration
// All services listed here are free-tier.
// ============================================================

/**
 * ICE server configuration.
 *
 * Primary path: direct peer-to-peer over STUN.
 * Fallback: Cloudflare Realtime TURN (1 TB/month free).
 *
 * IMPORTANT: Do NOT hardcode long-lived TURN credentials here.
 * TURN credentials must be fetched from the server at runtime
 * using the TURN REST API (short-lived ephemeral tokens).
 *
 * For local development without a Cloudflare account, only
 * STUN servers are used and direct P2P is attempted.
 */

export const ICE_SERVERS_STUN_ONLY: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

/**
 * Build full ICE server list including TURN credentials.
 * Call this from useWebRTC after fetching ephemeral TURN creds
 * from your Cloudflare Worker (never from the client directly).
 */
export function buildIceServers(turnCreds?: {
  urls: string;
  username: string;
  credential: string;
}): RTCIceServer[] {
  if (!turnCreds) return ICE_SERVERS_STUN_ONLY;
  return [
    ...ICE_SERVERS_STUN_ONLY,
    {
      urls: turnCreds.urls,
      username: turnCreds.username,
      credential: turnCreds.credential,
    },
  ];
}

export const RTC_CONFIG_BASE: RTCConfiguration = {
  iceTransportPolicy: 'all', // switch to 'relay' to force TURN
  bundlePolicy: 'max-bundle',
  rtcpMuxPolicy: 'require',
  iceCandidatePoolSize: 5,
};

/** Preferred video codec order. Browser will pick the first it supports. */
export const PREFERRED_VIDEO_CODECS = ['VP9', 'VP8', 'H264'];

/** Target constraints for high-quality streaming */
export const VIDEO_SENDER_PARAMS = {
  maxBitrate: 8_000_000,    // 8 Mbps max (1080p high quality)
  minBitrate: 500_000,      // 500 kbps min
  startBitrate: 3_000_000,  // 3 Mbps initial
  maxFramerate: 60,
};

export type VideoQuality = '1080p' | '720p' | '480p' | 'auto';

export interface VideoQualityPreset {
  scaleResolutionDownBy: number;
  maxBitrate: number;
  maxFramerate: number;
  degradationPreference: RTCDegradationPreference;
}

export const VIDEO_QUALITY_PRESETS: Record<VideoQuality, VideoQualityPreset> = {
  '1080p': {
    scaleResolutionDownBy: 1.0,
    maxBitrate: 8_000_000,
    maxFramerate: 60,
    degradationPreference: 'maintain-resolution',
  },
  '720p': {
    scaleResolutionDownBy: 1.5,
    maxBitrate: 3_500_000,
    maxFramerate: 60,
    degradationPreference: 'maintain-resolution',
  },
  '480p': {
    scaleResolutionDownBy: 2.25,
    maxBitrate: 1_200_000,
    maxFramerate: 30,
    degradationPreference: 'maintain-resolution',
  },
  'auto': {
    scaleResolutionDownBy: 1.0,
    maxBitrate: 8_000_000,
    maxFramerate: 60,
    degradationPreference: 'balanced',
  },
};

